import clsx from "clsx";
import type { FC } from "react";

import { PageList } from "./PageList";
import { PublicationActionChip } from "./PublicationActionChip";
import { CATEGORY_TRANSLATION_KEYS } from "./helpers";

import { ChangeAction, type TopicChangeSummary } from "api/api-types-publishing";
import { t } from "i18n/i18n";
import { AccordionPanel } from "mds/components/AccordionPanel";
import { IconText } from "mds/components/IconText";
import { PageListIcon } from "mds/icons";

interface TopicSummaryProps {
  topic: TopicChangeSummary;
  sectionId: string;
}

export const TopicSummary: FC<TopicSummaryProps> = ({ topic, sectionId }) => {
  const categories = [
    { key: "prep" as const, pages: topic.prep_pages },
    { key: "lesson" as const, pages: topic.lesson_pages },
    { key: "resource" as const, pages: topic.resource_pages },
    { key: "archive" as const, pages: topic.archive_pages },
  ].filter((cat) => cat.pages.length > 0);

  const isTopicDeleted = topic.action === ChangeAction.delete;
  const noTopicSummary = categories.length === 0;

  return (
    <AccordionPanel
      childrenWrapperClassName="gap-6"
      className="flex flex-col gap-6 rounded-[12px] px-6 py-4 supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
      collapsedColor="no-border-white"
      expandedColor="no-border-white"
      hideToggle={noTopicSummary}
      iconPosition="end"
      iconSize="xl"
      title={
        <IconText
          className={clsx("gap-3 text-base font-medium", isTopicDeleted && "text-black-tint-55")}
          iconEnd={<PublicationActionChip action={topic.action} />}
          iconStart={<PageListIcon />}
          text={<span className={isTopicDeleted ? "line-through" : ""}>{topic.title}</span>}
        />
      }
    >
      {categories.map((cat, index) => (
        <div key={cat.key}>
          <div className="mb-2 text-sm font-medium text-black-tint-40">
            {t(CATEGORY_TRANSLATION_KEYS[cat.key])}
          </div>

          <PageList
            pages={cat.pages}
            sectionId={sectionId}
            showTrailingDivider={index < categories.length - 1}
            skipFirstDivider={false}
          />
        </div>
      ))}
    </AccordionPanel>
  );
};
